import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { InvoiceStatus, type Invoice, type TenantId } from '@etiquetachef/types';
import { CancelInvoiceDialog } from './CancelInvoiceDialog';
import { RecordManualPaymentDialog } from './RecordManualPaymentDialog';
import { ResendInvoiceEmailDialog } from './ResendInvoiceEmailDialog';
import { openInvoicePdf } from '@/lib/invoiceDocument';

type Props = {
  tenantId: TenantId;
  invoice: Invoice;
};

type OpenDialog = 'pay' | 'cancel' | 'resend' | null;

/**
 * Barra de ações de uma fatura no admin: ver, baixar PDF, reenviar e-mail,
 * marcar como paga e cancelar — cada ação abre o diálogo correspondente.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (rotas
 * `/billing/*`, Sprint 16).
 *
 * Contrato: "Marcar como paga" só com `canPay` (não PAID/CANCELLED/REFUNDED);
 * "Cancelar" só em fatura PENDING — a mesma regra do `cancelInvoice`. Os diálogos ficam montados sempre e
 * abrem por `open`. As precondições de gateway (fatura do Stripe/Asaas) são
 * dos diálogos, não daqui.
 *
 * Permissões no servidor: `recordManualPayment` e `cancelInvoice` exigem
 * `ADMIN_PERMISSIONS.recordManualPayment`/`.cancelInvoice` (ROOT/FINANCE); a
 * barra não esconde botão por papel. O servidor só cancela fatura PENDING
 * (`admin/cancelInvoice.ts`); fatura de gateway (Stripe/Asaas) ele recusa antes
 * (`assertLocalInvoice`) com mensagem própria, então o botão segue visível nela.
 *
 * Usado por: `routes/InvoiceDetailPage.tsx` (`/billing/invoices/:tenantId/:invoiceId`,
 * aberta pela lista Cobrança → Faturas; spec `docs/screens/billing-invoices.md`),
 * admin web. A lista (`InvoicesPage`) usa o menu do `InvoiceTable` e os mesmos diálogos.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-13 (#935, item 7): "Cancelar" aparecia em OVERDUE, VOID e
 *    REFUNDED, e o servidor respondia `failed-precondition`.
 *  - 2026-09-29 (#776): "Baixar PDF" abria o `pdf_url` gravado, que no Stripe
 *    expira — hoje `openInvoicePdf` pede o link atual ao gateway.
 *
 * Histórico:
 *  - 2026-06-08 22:23 +0200 — Cloudscape (39-E).
 *  - 2026-09-29 14:18 +0200 — (#776) PDF pelo link atual (`openInvoicePdf`).
 *  - 2026-10-10 14:40 +0200 — (#798) permissões, spec e REFUNDED (#796).
 *  - 2026-10-13 10:28 +0200 — (#935, item 7) "Cancelar" só em PENDING.
 */
export function InvoiceActions({ tenantId, invoice }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState<OpenDialog>(null);
  const close = (): void => {
    setOpen(null);
  };

  const isPaid = invoice.status === InvoiceStatus.PAID;
  const isCancelled = invoice.status === InvoiceStatus.CANCELLED;
  const isRefunded = invoice.status === InvoiceStatus.REFUNDED;
  const canPay = !isPaid && !isCancelled && !isRefunded;
  // Bug corrigido em 2026-10-13 10:28 +0200 (#935, item 7): era `!isPaid &&
  // !isCancelled`, e o botão aparecia em OVERDUE/VOID/REFUNDED, que o
  // `cancelInvoice` recusa com `failed-precondition`. A regra tem de ser a do
  // servidor; mesma correção em `components/InvoiceTable.tsx`.
  const canCancel = invoice.status === InvoiceStatus.PENDING;

  const viewHref = `/billing/invoices/${tenantId}/${invoice.id}`;

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        variant="link"
        href={viewHref}
        onFollow={(e): void => {
          e.preventDefault();
          void navigate(viewHref);
        }}
      >
        {t('billing.invoiceActions.view')}
      </Button>

      {invoice.pdf_url && (
        // #776: link ATUAL do gateway — o `pdf_url` gravado do Stripe expira.
        <Button variant="link" iconAlign="right" iconName="external" onClick={(): void => { void openInvoicePdf(tenantId, invoice); }}>
          {t('billing.invoiceActions.downloadPdf')}
        </Button>
      )}

      <Button
        onClick={(): void => {
          setOpen('resend');
        }}
      >
        {t('billing.invoiceActions.resendEmail')}
      </Button>

      {canPay && (
        <Button
          variant="primary"
          onClick={(): void => {
            setOpen('pay');
          }}
        >
          {t('billing.invoiceActions.markPaid')}
        </Button>
      )}

      {canCancel && (
        <Button
          onClick={(): void => {
            setOpen('cancel');
          }}
        >
          {t('billing.invoiceActions.cancel')}
        </Button>
      )}

      <RecordManualPaymentDialog
        open={open === 'pay'}
        tenantId={tenantId}
        invoice={invoice}
        onClose={close}
      />
      <CancelInvoiceDialog
        open={open === 'cancel'}
        tenantId={tenantId}
        invoice={invoice}
        onClose={close}
      />
      <ResendInvoiceEmailDialog
        open={open === 'resend'}
        tenantId={tenantId}
        invoice={invoice}
        onClose={close}
      />
    </SpaceBetween>
  );
}
